import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

const CARD_WIDTH = 280;
const VIEWPORT_MARGIN = 12;

const truncatePhrase = (phrase) => {
  if (!phrase) return '';
  const cleaned = phrase.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned.length > 100 ? cleaned.substring(0, 100) + '...' : cleaned;
};

const SourceTooltip = ({ value, sources, verbatimPhrase, tableCaption, onViewClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [style, setStyle] = useState({});
  const wrapperRef = useRef(null);
  const cardRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  useLayoutEffect(() => {
    if (!isVisible || !anchorRect || !cardRef.current) return;

    const card = cardRef.current;
    const cardHeight = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceAbove = anchorRect.top;
    const spaceBelow = vh - anchorRect.bottom;
    let top;

    if (spaceBelow >= cardHeight + VIEWPORT_MARGIN) {
      top = anchorRect.bottom + 8;
    } else if (spaceAbove >= cardHeight + VIEWPORT_MARGIN) {
      top = anchorRect.top - cardHeight - 8;
    } else {
      if (spaceBelow >= spaceAbove) {
        top = anchorRect.bottom + 8;
      } else {
        top = anchorRect.top - cardHeight - 8;
      }
    }

    top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - cardHeight - VIEWPORT_MARGIN));

    let left = anchorRect.left;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - CARD_WIDTH - VIEWPORT_MARGIN));

    setStyle({ top, left, position: 'fixed' });
  }, [isVisible, anchorRect]);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(hideTimeoutRef.current);
    if (wrapperRef.current) {
      setAnchorRect(wrapperRef.current.getBoundingClientRect());
    }
    setIsVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 150);
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    clearTimeout(hideTimeoutRef.current);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 150);
  }, []);

  const truncated = truncatePhrase(verbatimPhrase);

  return (
    <span
      ref={wrapperRef}
      className="source-tooltip-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="source-tooltip-value">{value}</span>

      {isVisible && ReactDOM.createPortal(
        <div
          ref={cardRef}
          className="source-tooltip-card"
          style={style}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
        >
          {sources.map((source, index) => (
            <div
              key={index}
              className={`source-tooltip-item${index < sources.length - 1 ? ' source-tooltip-item-border' : ''}`}
            >
              <div className="source-tooltip-doc-name">
                <i className="bi bi-file-earmark-text me-1"></i>
                {source.doc_title
                  ? source.doc_title.length > 50
                    ? source.doc_title.substring(0, 50) + '...'
                    : source.doc_title
                  : `Document ${source.doc_id}`}
              </div>
              {truncated && (
                <div className="source-tooltip-phrase">"{truncated}"</div>
              )}
              {/* {tableCaption && (
                <div className="source-tooltip-caption">
                  <i className="bi bi-table me-1"></i>
                  {tableCaption.length > 60
                    ? tableCaption.substring(0, 60) + '...'
                    : tableCaption}
                </div>
              )} */}
              <button
                className="source-tooltip-view-btn"
                onClick={() => {
                  setIsVisible(false);
                  onViewClick && onViewClick(source, verbatimPhrase, tableCaption);
                }}
              >
                View <i className="bi bi-eye ms-1"></i>
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </span>
  );
};

export default SourceTooltip;